import { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';
import { validateApiResponse } from '../../lib/validateApiResponse';

interface BusinessOverview {
  content: string;
  updatedAt: string;
  updatedBy: string;
}

const API = '/api/partner/business-overview';

const mdComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, marginTop: 8, borderBottom: '1px solid var(--tn-border)', paddingBottom: 8 }}>{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8, marginTop: 20 }}>{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, marginTop: 16 }}>{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p style={{ marginBottom: 10, lineHeight: 1.6 }}>{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul style={{ paddingLeft: 20, marginBottom: 10 }}>{children}</ul>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li style={{ marginBottom: 4 }}>{children}</li>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 13 }}>{children}</table>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th style={{ textAlign: 'left', padding: '6px 10px', background: 'var(--tn-bg-secondary)', borderBottom: '1px solid var(--tn-border)', fontWeight: 600 }}>{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--tn-border)' }}>{children}</td>
  ),
};

export default function BusinessOverviewPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [overview, setOverview] = useState<BusinessOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetch(API, { signal: AbortSignal.timeout(15000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const data = validateApiResponse<BusinessOverview>(raw, '/api/partner/business-overview', {
        content: 'string',
        updatedAt: 'string',
        updatedBy: 'string',
      });
      setOverview(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const startEdit = useCallback(() => {
    setDraft(overview?.content ?? '');
    setSaveError('');
    setEditing(true);
  }, [overview]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await fetch(API, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: draft }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      setOverview(updated);
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.getTime() === 0
      ? 'noch nie'
      : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--tn-bg)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px',
        background: 'var(--tn-bg-dark)', borderBottom: '1px solid var(--tn-border)',
        height: 34, flexShrink: 0,
      }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--tn-cyan)' }}>BUSINESS-ÜBERSICHT</span>
        {overview && (
          <span style={{ fontSize: 11, color: 'var(--tn-text-muted)', flex: 1 }}>
            Zuletzt aktualisiert: {formatDate(overview.updatedAt)}{overview.updatedBy !== 'system' ? ` von ${overview.updatedBy}` : ''}
          </span>
        )}
        {!overview && <span style={{ flex: 1 }} />}
        {isAdmin && !editing && (
          <button data-ai-id="business-overview-edit" onClick={startEdit} style={primaryBtnStyle}>Bearbeiten</button>
        )}
        {isAdmin && editing && (
          <>
            <button data-ai-id="business-overview-cancel-edit" onClick={() => setEditing(false)} style={ghostBtnStyle}>Abbrechen</button>
            <button data-ai-id="business-overview-save" onClick={handleSave} disabled={saving} style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </>
        )}
      </div>

      {error && (
        <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--tnf-f7768e)', background: 'rgba(247,118,142,0.08)' }}>
          Fehler: {error}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--tn-text-muted)', fontSize: 12 }}>Lädt...</div>
        ) : editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <textarea
              data-ai-id="business-overview-draft-editor"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              style={{
                flex: 1, width: '100%', boxSizing: 'border-box', resize: 'vertical',
                background: 'var(--tn-surface)', color: 'var(--tn-text)',
                border: '1px solid var(--tn-border)', borderRadius: 4,
                padding: 10, fontSize: 13, fontFamily: 'monospace', minHeight: 300,
              }}
            />
            {saveError && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tnf-f7768e)' }}>{saveError}</div>}
          </div>
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents as Record<string, unknown>}>
            {overview?.content ?? ''}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: 'var(--tn-blue)', color: '#fff',
  border: 'none', borderRadius: 4,
  padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};

const ghostBtnStyle: React.CSSProperties = {
  background: 'transparent', color: 'var(--tn-text-muted)',
  border: '1px solid var(--tn-border)', borderRadius: 4,
  padding: '5px 10px', fontSize: 12, cursor: 'pointer',
};
